import React, { useEffect, useState } from "react";
import { Image, Pressable, ScrollView, Text, View } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import ReportGoodNewsCarousel from "../components/ReportGoodNewsCarousel";
import ReportCharacterRow from "../components/ReportCharacterRow";
import ReportBackground from "../components/ReportBackground";
import ReportModalSheet from "../components/ReportModalSheet";
import ReportIndicatorItem from "../components/ReportIndicatorItem";
import ReportEmptyState from "../components/ReportEmptyState";
import ReportCategoryTabs from "../components/ReportCategoryTabs";
import ReportSecondaryCategoryContent from "../components/ReportSecondaryCategoryContent";
import ReportLoadingState from "../components/ReportLoadingState";
import ReportPeriodSelector from "../components/ReportPeriodSelector";
import { getReportStatistic } from "../services/reportStatistics";
import { formatPeriod } from "../Utils/formatPeriod";
import { formatCharacterName } from "../Utils/formatReportText";
import {
  deriveReportDashboardData,
  reportCategoriesByMode,
  schoolsByMode,
} from "../Utils/reportDashboardData";
import styles from "./ReportDashboard.styles";
import {
  CalendarTodayIcon,
  DangerIcon,
  Heart2Icon,
  PersonSearchIcon,
  FactCheckIcon,
  ExtensionIcon,
  ThumbUpIcon,
  ProfileIcon,
  PaperOnlyIcon,
  MenuIcon,
} from "../assets/icons";

export default function ReportDashboard() {
  const navigation = useNavigation();
  const route = useRoute();
  const reportId = route.params?.id;
  const mode = route.params?.mode || "";
  const school = Object.prototype.hasOwnProperty.call(schoolsByMode, mode)
    ? schoolsByMode[mode]
    : schoolsByMode.default;
  const [statistic, setStatistic] = useState(null);
  const [period, setPeriod] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [charactersOpen, setCharactersOpen] = useState(false);
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [nonAcademicInfoOpen, setNonAcademicInfoOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [indicatorType, setIndicatorType] = useState("positive");
  const [selectedCategoryKey, setSelectedCategory] = useState("character");
  const categories = Object.prototype.hasOwnProperty.call(reportCategoriesByMode, mode)
    ? reportCategoriesByMode[mode]
    : reportCategoriesByMode.default;
  const activeCategory =
    categories.find((category) => category.key === selectedCategoryKey) || categories[0];
  const selectedCategory = activeCategory.key;
  const CategoryContent = activeCategory.Content;
  const reportCategory = activeCategory.report || selectedCategory;
  useEffect(() => {
    if (!reportId) {
      setLoadError(true);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setLoadError(false);
    getReportStatistic(reportId, mode, reportCategory)
      .then(({ data }) => {
        if (!active) return;
        setStatistic(data);
        setSelectedChild(data?.siswa || "");
        setSelectedProfile(data?.chosenProfile || "");
        setPeriod(data?.periode?.[0] || "");
      })
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reportId, mode, reportCategory]);

  const periods = statistic?.periode || [];
  const summary =
    statistic?.summaries?.find((item) => item.periode === period) ||
    statistic?.summaries?.[0];
  const {
    allCharacters,
    charactersWithData,
    characterData,
    indicatorData,
    characterThreshold,
    indicatorThreshold,
    passedCount,
    failedCount,
    hasThresholdData,
    progress,
  } = deriveReportDashboardData(summary);

  const openCharacter = (character) => {
    if (!character) return;
    navigation.navigate("ReportIndicatorDetail", {
      character,
      summary,
      statistic,
      period,
      id: reportId,
    });
  };
  const openAllIndicators = () =>
    navigation.navigate("ReportIndicatorDetail", {
      summary,
      statistic,
      period,
      id: reportId,
      showAllIndicators: true,
    });

  if (loading && !statistic) {
    return <ReportLoadingState />;
  }

  return (
    <ReportBackground>
      <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
        <View style={styles.headerActions}>
          <View style={styles.avatar}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Pilih data anak"
              onPress={() => setChildPickerOpen(true)}
            >
              <PersonSearchIcon width={24} height={24} />
            </Pressable>
          </View>
          <Pressable
            style={styles.inputData}
            onPress={() =>
              navigation.navigate("ReportInputData", {
                statistic,
                summary,
                period,
                id: reportId,
              })
            }
          >
            <PaperOnlyIcon width={18} height={18} />
            <Text style={styles.inputDataText}>Input Data</Text>
          </Pressable>
        </View>

        <View style={styles.greeting}>
          <Text style={styles.greetingTitle}>Halo Ayah Bunda</Text>
          <Text style={styles.greetingSubtitle}>Berikut Laporan Ananda {selectedChild}</Text>
        </View>

        {loadError && (
          <Text style={styles.apiMessage}>Laporan belum dapat dimuat.</Text>
        )}

        <View style={styles.schoolPeriodCard}>
          <View style={[styles.schoolInfo, styles[school.infoStyle]]}>
            <Image
              source={school.logo}
              style={[styles.schoolLogo, styles[school.logoStyle]]}
              resizeMode="contain"
            />
            <View style={styles.schoolDivider} />
            <Text style={styles.schoolName}>{school.name}</Text>
          </View>
          <View style={styles.schoolPeriodDivider} />
          <ReportPeriodSelector
            period={period}
            disabled={periods.length === 0}
            onPress={() => setPeriodOpen(true)}
          />
        </View>

        {mode !== "" && (
          <ReportCategoryTabs
            tabs={categories}
            selectedKey={selectedCategory}
            onSelect={(key) => {
              if (mode === "nihiis" && key === "materials") {
                setNonAcademicInfoOpen(true);
                return;
              }
              setSelectedCategory(key);
            }}
          />
        )}

        {selectedCategory === "character" ? (
          <>
            <View style={styles.card}>
              <View style={styles.sectionHeading}>
                <View style={styles.iconTile}>
                  <FactCheckIcon customColor="#5423B5" width={18} height={18} />
                </View>
                <Text style={styles.sectionTitle}>Pencapaian Karakter</Text>
                <Pressable
                  onPress={() => setCharactersOpen(true)}
                  style={styles.seeAllButton}
                >
                  <Text style={styles.seeAll}>Lihat Semua</Text>
                </Pressable>
              </View>
              <View style={styles.progressTrack}>
                <View
                  style={[styles.progressValue, { width: `${progress}%` }]}
                />
              </View>
              {loadError ? (
                <ReportEmptyState
                  compact
                  title="Pencapaian gagal dimuat"
                  description="Coba kembali beberapa saat lagi."
                />
              ) : hasThresholdData ? (
                <View style={styles.progressLabels}>
                  <Text style={styles.bodyText}>
                    <Text style={styles.bold}>{progress}%</Text> Tercapai
                  </Text>
                  <Text style={styles.bodyText}>
                    {failedCount} Perlu Perhatian
                  </Text>
                </View>
              ) : (
                <ReportEmptyState
                  compact
                  title="Belum ada data pencapaian"
                  description="Data karakter dan indikator belum tersedia untuk periode ini."
                />
              )}
              <View style={styles.statsDivider} />
              {!loadError && (
              <View style={styles.statColumns}>
                <View style={styles.statColumn}>
                  <View style={styles.statLabelRow}>
                    <View style={styles.smallIconTile}>
                      <Heart2Icon customColor="#151515" />
                    </View>
                    <Text style={styles.statLabel}>Karakter</Text>
                  </View>
                  <Text style={styles.statValue}>
                    {characterThreshold.passed || 0} dari{" "}
                    {(characterThreshold.passed || 0) +
                      (characterThreshold.failed || 0)}
                  </Text>
                  <Text style={styles.achieved}>
                    {characterThreshold.passed || characterThreshold.failed
                      ? "Sudah Tercapai"
                      : "Belum ada data"}
                  </Text>
                </View>
                <View style={styles.statColumn}>
                  <View style={styles.statLabelRow}>
                    <View style={styles.smallIconTile}>
                      <ExtensionIcon width={18} height={18} />
                    </View>
                    <Text style={styles.statLabel}>Indikator</Text>
                  </View>
                  <Text style={styles.statValue}>
                    {indicatorThreshold.passed || 0} dari{" "}
                    {(indicatorThreshold.passed || 0) +
                      (indicatorThreshold.failed || 0)}
                  </Text>
                  <Text style={styles.achieved}>
                    {indicatorThreshold.passed || indicatorThreshold.failed
                      ? "Sudah Tercapai"
                      : "Belum ada data"}
                  </Text>
                </View>
              </View>
              )}
            </View>

            <ReportGoodNewsCarousel indicatorData={indicatorData} />

            <View style={styles.card}>
              <View style={styles.sectionHeading}>
                <View style={styles.iconTile}>
                  <Heart2Icon customColor="#5423B5" />
                </View>
                <Text style={styles.sectionTitle}>TOP 3 Karakter Terbaik</Text>
                <Pressable
                  onPress={() => setCharactersOpen(true)}
                  style={styles.seeAllButton}
                >
                  <Text style={styles.seeAll}>Lihat Semua</Text>
                </Pressable>
              </View>
              <View style={styles.listDivider} />
              {loadError ? (
                <ReportEmptyState
                  title="Karakter gagal dimuat"
                  description="Coba kembali beberapa saat lagi."
                />
              ) : characterData.length === 0 ? (
                <ReportEmptyState
                  title="Belum ada data karakter"
                  description="Karakter belum memiliki indikator pada periode ini."
                />
              ) : characterData.map((item) => (
                <ReportCharacterRow
                  key={item.name}
                  item={item}
                  onPress={() => openCharacter(item.name)}
                />
              ))}
            </View>

            <View style={styles.card}>
              <View style={styles.sectionHeading}>
                <View style={styles.iconTile}>
                  <ExtensionIcon customColor="#5423B5" width={18} height={18} />
                </View>
                <Text style={styles.sectionTitle}>TOP 3 Indikator</Text>
                <Pressable
                  onPress={openAllIndicators}
                  style={styles.seeAllButton}
                >
                  <Text style={styles.seeAll}>Lihat Semua</Text>
                </Pressable>
              </View>
              <View style={styles.segmentRow}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{
                    selected: indicatorType === "positive",
                  }}
                  onPress={() => setIndicatorType("positive")}
                  style={[
                    styles.segment,
                    indicatorType === "positive" &&
                      styles.segmentPositiveActive,
                  ]}
                >
                  <ThumbUpIcon
                    width={22}
                    height={22}
                    customColor={
                      indicatorType === "positive" ? "#48B920" : "#151515"
                    }
                  />
                  <Text
                    style={[
                      styles.segmentText,
                      indicatorType === "positive" && styles.positiveText,
                    ]}
                  >
                    Perilaku{"\n"}Terbaik
                  </Text>
                  {indicatorType === "positive" && (
                    <View
                      style={[
                        styles.segmentUnderline,
                        styles.positiveUnderline,
                      ]}
                    />
                  )}
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{
                    selected: indicatorType === "attention",
                  }}
                  onPress={() => setIndicatorType("attention")}
                  style={[
                    styles.segment,
                    indicatorType === "attention" &&
                      styles.segmentAttentionActive,
                  ]}
                >
                  <DangerIcon
                    width={22}
                    height={22}
                    fill={indicatorType === "attention" ? "#D84343" : "#151515"}
                  />
                  <Text
                    style={[
                      styles.segmentText,
                      indicatorType === "attention" && styles.attentionText,
                    ]}
                  >
                    Perlu{"\n"}Perhatian
                  </Text>
                  {indicatorType === "attention" && (
                    <View
                      style={[
                        styles.segmentUnderline,
                        styles.attentionUnderline,
                      ]}
                    />
                  )}
                </Pressable>
              </View>
              {loadError && (
                <ReportEmptyState
                  title="Data indikator tidak tersedia"
                  description="Coba muat ulang laporan nanti."
                />
              )}
              {!loadError &&
                indicatorData[indicatorType].length === 0 && (
                  <ReportEmptyState
                    title="Belum ada indikator"
                    description={`Belum ada indikator ${
                      indicatorType === "positive"
                        ? "terbaik"
                        : "perlu perhatian"
                    } untuk periode ini.`}
                  />
                )}

                {!loadError && indicatorData[indicatorType].map((item, index) => {
                if (
                  indicatorType === "positive" &&
                  item.value &&
                  item.value >= 3
                ) {
                  return (
                    <ReportIndicatorItem
                      key={`${item.name}-${index}`}
                      item={item}
                      characterName={item.characterName}
                      compact
                      isLast={index === indicatorData[indicatorType].length - 1}
                    />
                  );
                }

                if (indicatorType === "attention") {
                  return (
                    <ReportIndicatorItem
                      key={`${item.name}-${index}`}
                      item={item}
                      characterName={item.characterName}
                      compact
                      isLast={index === indicatorData[indicatorType].length - 1}
                    />
                  );
                }
              })}
            </View>
          </>
        ) : CategoryContent ? (
          <CategoryContent
            reportId={reportId}
            mode={mode}
            period={period}
            statistic={statistic}
            loading={loading}
            loadError={loadError}
          />
        ) : (
          <ReportSecondaryCategoryContent
            category={selectedCategory}
            statistic={statistic}
            period={period}
            reportId={reportId}
            mode={mode}
            loading={loading && selectedCategory === "others"}
            loadError={loadError}
          />
        )}
      </ScrollView>
      <ReportModalSheet
        visible={periodOpen}
        onClose={() => setPeriodOpen(false)}
        Icon={CalendarTodayIcon}
        title="Periode"
        iconColor="#6724E2"
      >
        {periods.length === 0 ? (
          <ReportEmptyState
            title="Belum ada periode"
            description="Periode laporan belum tersedia saat ini."
          />
        ) : (
          periods.map((month) => (
            <Pressable
              key={month}
              onPress={() => {
                setPeriod(month);
                setPeriodOpen(false);
              }}
              style={[
                styles.periodChoice,
                period === month && styles.periodChoiceSelected,
              ]}
            >
              <Text
                style={[
                  styles.periodChoiceText,
                  period === month && styles.periodChoiceTextSelected,
                ]}
              >
                {formatPeriod(month)}
              </Text>
              {period === month && (
                <View style={styles.selectedCheck}>
                  <Text style={styles.selectedCheckText}>✓</Text>
                </View>
              )}
            </Pressable>
          ))
        )}
      </ReportModalSheet>
      <ReportModalSheet
        visible={childPickerOpen}
        onClose={() => setChildPickerOpen(false)}
        Icon={PersonSearchIcon}
        title="Pilih Data Anak"
      >
        {selectedChild ? (
          <Pressable
            accessibilityRole="radio"
            accessibilityState={{ selected: true }}
            onPress={() => setChildPickerOpen(false)}
            style={styles.childChoice}
          >
            <View style={styles.childAvatar}>
              <ProfileIcon width={18} height={18} />
            </View>
            <View style={styles.childDetails}>
              <Text style={styles.childName}>
                {formatCharacterName(selectedChild)}
              </Text>
              <Text style={styles.childClass}>
                Kelas {formatCharacterName(selectedProfile)}
              </Text>
            </View>
            <View style={styles.selectedCheck}>
              <Text style={styles.selectedCheckText}>✓</Text>
            </View>
          </Pressable>
        ) : (
          <ReportEmptyState
            title="Belum ada data anak"
            description="Data anak belum tersedia untuk laporan ini."
          />
        )}
      </ReportModalSheet>
      <ReportModalSheet
        visible={charactersOpen}
        onClose={() => setCharactersOpen(false)}
        contentStyle={styles.characterSheet}
        Icon={Heart2Icon}
        title="Pencapaian Karakter"
      >
        <ScrollView
          style={styles.characterSheetList}
          showsVerticalScrollIndicator={false}
        >
          {loading || loadError || charactersWithData.length === 0 ? (
            <ReportEmptyState
              title={
                loading
                  ? "Memuat karakter"
                  : loadError
                    ? "Data karakter tidak tersedia"
                    : "Belum ada data karakter"
              }
              description={
                loading
                  ? "Data karakter sedang dimuat."
                  : loadError
                    ? "Coba muat ulang laporan nanti."
                    : "Karakter belum memiliki indikator pada periode ini."
              }
            />
          ) : (
            charactersWithData.map((item) => (
              <ReportCharacterRow
                key={item.name}
                item={item}
                onPress={() => {
                  setCharactersOpen(false);
                  openCharacter(item.name);
                }}
              />
            ))
          )}
        </ScrollView>
      </ReportModalSheet>
      <ReportModalSheet
        visible={nonAcademicInfoOpen}
        onClose={() => setNonAcademicInfoOpen(false)}
        contentStyle={styles.nonAcademicInfoSheet}
        Icon={MenuIcon}
        title="Menu"
        iconColor="#6724E2"
      >
        <View style={styles.nonAcademicInfoContent}>
          <View style={styles.largeMenuGlyph}>
            <MenuIcon width={24} height={24} customColor="#FFFFFF" />
          </View>
          <Text style={styles.nonAcademicInfoTitle}>Menu Belum Tersedia</Text>
          <Text style={styles.nonAcademicInfoDescription}>
            Mohon menunggu. Fitur ini akan segera hadir dalam beberapa bulan ke depan.
          </Text>
        </View>
      </ReportModalSheet>
    </ReportBackground>
  );
}
